import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AdminModal } from './AdminModal';

type Props = {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: ReactNode;
    confirmLabel?: string;
    onConfirm: () => void | Promise<void>;
    loading?: boolean;
    zIndexBase?: number;
};

export function DeleteConfirmModal({
    open,
    onClose,
    title,
    description,
    confirmLabel = 'Delete',
    onConfirm,
    loading = false,
    zIndexBase = 1000,
}: Props) {
    const handleClose = () => {
        if (!loading) onClose();
    };
    return (
        <AdminModal
            open={open}
            onClose={handleClose}
            closeOnOverlayClick={!loading}
            closeOnEscape={!loading}
            zIndexBase={zIndexBase}
        >
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 opacity-100 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
                <div className="mb-4 flex justify-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
                        <svg
                            className="h-6 w-6 text-red-600 dark:text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                </div>
                <h3 className="mb-2 text-center font-nunito text-[15px] font-bold tracking-[-0.01em] text-slate-900 dark:text-white">{title}</h3>
                {description ? (
                    <div className="mb-6 text-center text-sm text-slate-600 dark:text-slate-300">{description}</div>
                ) : (
                    <div className="mb-6" />
                )}
                <div className="flex flex-col-reverse justify-center gap-3 sm:flex-row sm:gap-0 sm:space-x-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-[#FFFFFF0F] dark:text-slate-300 dark:hover:bg-white/5"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void onConfirm()}
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-error-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-error-700 disabled:opacity-50"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </div>
            </div>
        </AdminModal>
    );
}
